/**
 * Lightweight SVG chart primitives for the Masters stats modal — donut, bar
 * list, multi-series area trend, progress bar, legend + value formatters. Zero
 * dependencies, themed to the dark glassy planner UI (mirrors the CapacityPanel
 * sparkline technique). Hover interactions for exact figures.
 */
import { useState } from 'react'
import { Info } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

export const CHART_COLORS = [
  '#38bdf8', // sky
  '#a78bfa', // violet
  '#34d399', // emerald
  '#fbbf24', // amber
  '#f87171', // red
  '#f472b6', // pink
  '#60a5fa', // blue
  '#facc15', // yellow
]

export function formatUsd(n: number, compact = false): string {
  const abs = Math.abs(n)
  if (compact && abs >= 1000) {
    if (abs >= 1e6) return `$${(n / 1e6).toFixed(abs >= 1e7 ? 1 : 2)}M`
    return `$${Math.round(n / 1e3)}K`
  }
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
}

export function formatNum(n: number): string {
  return Math.round(n).toLocaleString('en-US')
}

interface Slice {
  label: string
  value: number
  color?: string
}

/** Donut with hover-to-inspect; center shows a default label/value when idle. */
export function Donut({
  data,
  size = 168,
  thickness = 20,
  centerLabel,
  centerValue,
}: {
  data: Slice[]
  size?: number
  thickness?: number
  centerLabel?: string
  centerValue?: string
}) {
  const [hover, setHover] = useState<number | null>(null)
  const total = data.reduce((s, d) => s + d.value, 0) || 1
  const r = (size - thickness) / 2
  const cx = size / 2
  const cy = size / 2
  const C = 2 * Math.PI * r
  let offset = 0

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={thickness} />
        {data.map((d, i) => {
          const len = (d.value / total) * C
          const color = d.color ?? CHART_COLORS[i % CHART_COLORS.length]
          const node = (
            <circle
              key={d.label}
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke={color}
              strokeWidth={hover === i ? thickness + 4 : thickness}
              strokeDasharray={`${len} ${C - len}`}
              strokeDashoffset={-offset}
              className="cursor-pointer transition-all"
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
            />
          )
          offset += len
          return node
        })}
      </svg>
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center">
        {hover !== null ? (
          <>
            <div className="text-base font-bold text-white tabular-nums">{formatNum(data[hover].value)}</div>
            <div className="max-w-[80%] text-[10px] leading-tight text-white/60">{data[hover].label}</div>
            <div className="text-[10px] text-white/40">{Math.round((data[hover].value / total) * 100)}%</div>
          </>
        ) : (
          <>
            <div className="text-2xl font-bold tabular-nums text-white">{centerValue}</div>
            <div className="text-[10px] uppercase tracking-wide text-white/40">{centerLabel}</div>
          </>
        )}
      </div>
    </div>
  )
}

/** Horizontal bar list (width relative to the max), each labeled with its value. */
export function BarList({
  data,
  format = formatNum,
  color,
}: {
  data: Slice[]
  format?: (n: number) => string
  color?: string
}) {
  const max = Math.max(...data.map((d) => d.value), 1)
  if (data.length === 0) return <div className="text-[11px] text-white/30">No data in this period.</div>
  return (
    <div className="space-y-2">
      {data.map((d, i) => (
        <div key={d.label}>
          <div className="mb-0.5 flex items-center justify-between text-[11px]">
            <span className="text-white/70">{d.label}</span>
            <span className="font-medium tabular-nums text-white/90">{format(d.value)}</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-white/5">
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${(d.value / max) * 100}%`, background: d.color ?? color ?? CHART_COLORS[i % CHART_COLORS.length] }}
            />
          </div>
        </div>
      ))}
    </div>
  )
}

export interface TrendSeries {
  label: string
  color: string
  values: number[]
}

/** Multi-series filled area trend with a hover guide + per-series readout. */
export function TrendChart({
  series,
  xLabels,
  height = 130,
  formatVal = formatNum,
}: {
  series: TrendSeries[]
  xLabels?: [string, string]
  height?: number
  formatVal?: (n: number) => string
}) {
  const [hoverX, setHoverX] = useState<number | null>(null)
  const n = series[0]?.values.length ?? 0
  const W = 600
  const H = height
  const max = Math.max(1, ...series.flatMap((s) => s.values))
  const xAt = (i: number) => (n > 1 ? (i / (n - 1)) * W : 0)
  const yAt = (v: number) => H - (v / max) * H
  const areaPath = (vals: number[]) =>
    vals.length ? `M0,${H} ` + vals.map((v, i) => `L${xAt(i).toFixed(1)},${yAt(v).toFixed(1)}`).join(' ') + ` L${W},${H} Z` : ''

  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const frac = (e.clientX - rect.left) / rect.width
    setHoverX(Math.max(0, Math.min(n - 1, Math.round(frac * (n - 1)))))
  }

  return (
    <div>
      <div className="relative">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="w-full"
          style={{ height }}
          preserveAspectRatio="none"
          onMouseMove={onMove}
          onMouseLeave={() => setHoverX(null)}
        >
          {series.map((s) => (
            <path key={s.label} d={areaPath(s.values)} fill={`${s.color}2e`} stroke={s.color} strokeWidth={1.5} />
          ))}
          {hoverX !== null && (
            <line x1={xAt(hoverX)} y1={0} x2={xAt(hoverX)} y2={H} stroke="white" strokeWidth={1} opacity={0.4} />
          )}
        </svg>
      </div>
      <div className="mt-1 flex items-center justify-between text-[9px] text-white/30">
        <span>{xLabels?.[0]}</span>
        {hoverX !== null && (
          <span className="flex gap-2">
            {series.map((s) => (
              <span key={s.label} style={{ color: s.color }} className="tabular-nums">
                {s.label} {formatVal(s.values[hoverX] ?? 0)}
              </span>
            ))}
          </span>
        )}
        <span>{xLabels?.[1]}</span>
      </div>
    </div>
  )
}

/** Slim utilization bar; colour ramps green → amber → red by fullness. */
export function ProgressBar({ pct, color }: { pct: number; color?: string }) {
  const c = color ?? (pct >= 95 ? '#f87171' : pct >= 70 ? '#fbbf24' : '#34d399')
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/5">
      <div className="h-full rounded-full" style={{ width: `${Math.min(100, pct)}%`, background: c }} />
    </div>
  )
}

/**
 * Dark-themed "(i)" info popover for explaining a derived metric. Uses the same
 * Radix Popover primitive as the rest of the app, so it nests safely inside the
 * stats Dialog (child dismissable layer — clicking it won't close the modal).
 */
export function InfoDot({ text }: { text: string }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="About this metric"
          className="inline-flex shrink-0 rounded-full text-white/30 transition-colors hover:text-white/70 focus-visible:outline-none"
        >
          <Info className="h-3 w-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="z-[60] w-64 border-white/10 bg-slate-900 text-xs leading-snug text-white/80 shadow-xl"
      >
        {text}
      </PopoverContent>
    </Popover>
  )
}

export function Legend({ items }: { items: Slice[] }) {
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1">
      {items.map((it, i) => (
        <div key={it.label} className="flex items-center gap-1.5 text-[11px] text-white/60">
          <span className="h-2 w-2 rounded-sm" style={{ background: it.color ?? CHART_COLORS[i % CHART_COLORS.length] }} />
          {it.label}
        </div>
      ))}
    </div>
  )
}
